import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, CreditCard, ImagePlus, Loader2, Lock, Save } from "lucide-react";
import BackOffice from "@/components/account/BackOffice";
import { Btn, FieldRow, PageHead, Section, TONES, Toggle, inputCls } from "@/components/account/Shell";
import api, { formatApiError, mediaUrl } from "@/lib/api";
import { GreenButton } from "@/components/Buttons";
import { uploadArtwork } from "@/components/signs/useSignConfig";
import { DEFAULT_THEME, themeOf } from "@/lib/mall";
import { MENU_ITEMS } from "@/lib/menu";

const TONE = TONES.settings;

/** [key, label, placeholder, one-line hint] — every basic fact about the shop, one per line. */
const FIELDS = [
    ["name", "Shop name", "Sunset Graphics", "Exactly as it should read in your header and on your shop card."],
    ["tagline", "One-liner", "Screen printing + embroidery in Sunset Park", "One sentence under your name. Shows on your card and billboard."],
    ["hours", "Hours", "Mon–Sat 10am–7pm", "When the shop is open."],
    ["turnaround", "Turnaround", "3–5 business days, rush available", "The promise shoppers see before they order."],
    ["address", "Street address", "1309 78th Street, Brooklyn NY 11228", "Shows on your banner and the map."],
    ["pickup_address", "Pickup or ship-from address", "5th Ave & 45th St · Brooklyn", "Where orders are picked up or shipped from, if different."],
    ["pickup_hours", "Pickup hours", "Locker wall, open 24 hours", "When someone can come get an order."],
    ["phone", "Contact phone", "(347) 555-0123", "Shoppers can tap to call."],
    ["city", "City, State", "Brooklyn, NY", "Used in the directory and on every card."],
    ["notification_email", "Notification email", "orders@yourshop.com", "Where orders and messages land. Blank = your login email."],
];

const BRAND = [
    ["logo", "Logo", "Sits in your header and on your shop card.", "h-14 w-14"],
    ["icon", "Icon", "The small square version.", "h-14 w-14"],
    ["favicon", "Favicon", "The browser-tab mark.", "h-14 w-14"],
    ["banner", "Main image", "The big photo behind your shop page and billboards.", "h-14 w-28"],
    ["storefront_photo", "Storefront photo", "Your shop from the street, for the directory.", "h-14 w-28"],
];

const SHIPPING = [
    ["", "Not set"],
    ["pickup_only", "Pickup only"],
    ["ships_3", "Ships in 3 days"],
    ["ships_5", "Ships in 5 days"],
    ["ships_10", "Ships in 10 days"],
];

const SOCIALS = [
    ["instagram", "Instagram", "@sunsetgraphicsbk"],
    ["facebook", "Facebook", "facebook.com/sunsetgraphics"],
    ["tiktok", "TikTok", "@sunsetgraphics"],
    ["youtube", "YouTube", "youtube.com/@sunsetgraphics"],
    ["x_handle", "X (Twitter)", "@sunsetgraphics"],
    ["pinterest", "Pinterest", "pinterest.com/sunsetgraphics"],
    ["linkedin", "LinkedIn", "linkedin.com/company/sunsetgraphics"],
    ["snapchat", "Snapchat", "sunsetgraphics"],
    ["website_url", "Your own website", "sunsetgraphics.com"],
];

const PICKUP_FLAGS = [
    ["pickup_enabled", "Customers can pick up at my shop", "Turns on pickup at checkout."],
    ["curbside", "I'll bring it out to the car", "Says so on your banner."],
    ["dropoff_enabled", "Customers can drop off blanks or artwork", "Shows a drop-off line on your page."],
    ["local_delivery", "I deliver locally", "Adds local delivery at checkout."],
];

const PICKUP_FIELDS = [
    ["pickup_instructions", "Where to go when they get there", "Side door, ring the bell"],
    ["pickup_ready_note", "When it's ready to grab", "Same day if it's in by noon"],
    ["local_delivery_fee", "Local delivery fee ($)", "15"],
    ["local_delivery_note", "How far you'll drive", "Anywhere in Brooklyn, free over $250"],
];

const SHIP_FIELDS = [
    ["ship_carriers", "Who you ship with", "USPS, UPS Ground"],
    ["ship_flat_fee", "Flat shipping fee ($)", "9.50"],
    ["ship_free_over", "Free shipping over ($)", "150"],
    ["ship_processing", "Time on the bench before it ships", "1–2 business days"],
    ["ship_note", "Anything else about shipping", "We can ship to your customer for you — just ask"],
];

const JUMPS = [
    ["brand", "Brand kit"], ["basics", "Basics"], ["counters", "What I sell"], ["ops", "Pickup"], ["shipping", "Shipping"],
    ["socials", "Socials"], ["billboard", "Billboard"], ["paint", "Colors"], ["money", "Money"],
];

export default function VendorSetup() {
    const [me, setMe] = useState(null);
    const [form, setForm] = useState({ name: "", tagline: "", about: "", hours: "", turnaround: "", pickup_address: "", pickup_instructions: "", policies: "", phone: "", city: "", logo: null, banner: null, address: "", pickup_hours: "", pickup_enabled: false, dropoff_enabled: false, shipping_mode: "" });
    const [theme, setTheme] = useState(DEFAULT_THEME);
    const [featured, setFeatured] = useState({ kind: "", slug: "", headline: "" });
    const [slots, setSlots] = useState({ billboard: true, products: true, website: true, artwork: true });
    const [options, setOptions] = useState([]);
    const [lineDefs, setLineDefs] = useState([]);
    const [lines, setLines] = useState({});
    const [busy, setBusy] = useState(false);
    const [uploading, setUploading] = useState(null);
    const refs = { logo: useRef(null), icon: useRef(null), favicon: useRef(null), banner: useRef(null), storefront_photo: useRef(null) };

    useEffect(() => {
        api.get("/stores/lines").then(({ data }) => setLineDefs(data.lines)).catch(() => { });
        api.get("/stores/me").then(({ data }) => {
            setMe(data);
            if (!data.store) return;
            // mirror the backend's ops_of() so the form shows what the banner actually says
            setForm((f) => ({
                ...f, ...data.store,
                address: data.store.address || data.store.pickup_address || "",
                pickup_enabled: data.store.pickup_enabled ?? Boolean(data.store.pickup_address),
                pickup_hours: data.store.pickup_hours || data.store.hours || "",
                shipping_mode: data.store.shipping_mode || (data.store.ships ? "ships_5" : ""),
                featured_home: data.store.featured_home !== false,
                builds_websites: !!data.store.builds_websites,
            }));
            setTheme(themeOf(data.store));
            setLines({ ...(data.store.lines || {}) });
            setSlots((s) => ({ ...s, ...(data.slots || {}) }));
            const f = data.store.featured || {};
            setFeatured({ kind: f.kind || "", slug: f.slug || "", headline: f.headline || "" });
            api.get(`/stores/${data.store.slug}`).then(({ data: page }) => setOptions([
                { kind: "gang", slug: "", label: "DTF gang sheets (designer)" },
                ...(page.shirts || []).map((p) => ({ kind: "shirt", slug: p.slug, label: `Apparel · ${p.name}` })),
                ...(page.signs || []).map((p) => ({ kind: "sign", slug: p.slug, label: `Signs · ${p.name}` })),
            ])).catch(() => { });
        }).catch(() => { });
    }, []);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const flag = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

    const pick = async (kind, file) => {
        if (!file) return;
        setUploading(kind);
        try {
            const rec = await uploadArtwork(file);
            setForm((f) => ({ ...f, [kind]: rec.url }));
        } catch { toast.error("Upload failed"); }
        finally { setUploading(null); }
    };

    const save = async (e) => {
        e?.preventDefault();
        if (!form.name?.trim()) { toast.error("Your shop needs a name"); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/stores/apply", {
                ...form, tier: me?.tier?.id || "kiosk", crew_perks_honored: !!form.crew_perks_honored,
                theme_primary: theme.primary, theme_secondary: theme.secondary, theme_tertiary: theme.tertiary,
                featured_kind: featured.kind || null, featured_slug: featured.slug || null,
                featured_headline: featured.headline || "",
                featured_home: !!form.featured_home, builds_websites: !!form.builds_websites,
                lines: Object.keys(lines).length ? lines : null,
                slot_billboard: slots.billboard, slot_products: slots.products,
                slot_website: slots.website, slot_artwork: slots.artwork,
            });
            toast.success("Store saved");
            if (data.warning) toast.warning(data.warning, { duration: 9000 });
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    if (me && me.tier?.id === "customer" && !me.store) {
        return (
            <BackOffice>
                <div className="mx-auto max-w-2xl py-20 text-center" data-testid="setup-locked">
                    <Lock size={32} className="mx-auto text-zinc-400" />
                    <h1 className="mt-3 font-display text-2xl font-black tracking-tight text-zinc-900">Storefront pages come with a bigger plan.</h1>
                    <p className="mt-2 text-sm text-zinc-800">
                        You don't have a plan yet. Open a Starter ($9 a month), a Storefront ($29 a month) or a Pro Shop ($79 a month)
                        and you get a shop page with logo, banner, hours, turnaround and pickup spots.
                    </p>
                    <Link to="/account/sell" className="mt-4 inline-block"><GreenButton data-testid="setup-upgrade">See the spaces</GreenButton></Link>
                </div>
            </BackOffice>
        );
    }

    const text = (key, ph, testid) => (
        <input className={inputCls} placeholder={ph} value={form[key] ?? ""} onChange={set(key)} data-testid={testid || `setup-${key}`} />
    );

    return (
        <BackOffice>
            <PageHead kicker="Profile" tone={TONE} title="Store profile" testid="setup-title"
                sub="Everything about your shop, one setting per line, top to bottom. This is the page customers land on inside the directory; your logo, one-liner and contact block also become your footer block."
                right={
                    <Link to="/account/homepage" data-testid="setup-homepage-link"
                        className="inline-flex items-center gap-1.5 rounded-full border border-black/15 bg-white px-3 py-1 font-mono text-[11px] font-bold uppercase tracking-[0.12em] text-zinc-800 hover:border-zinc-900">
                        Your homepage window →
                    </Link>
                } />

            <div className="mt-2 flex flex-wrap gap-1.5 border-b border-black/10 pb-2" data-testid="setup-jump">
                {JUMPS.map(([id, label]) => (
                    <a key={id} href={`#${id}`} data-testid={`setup-jump-${id}`}
                        className="rounded-full border border-black/15 bg-white px-2.5 py-0.5 font-mono text-[11px] font-bold uppercase tracking-[0.12em] text-zinc-800 hover:border-zinc-900">
                        {label}
                    </a>
                ))}
            </div>

            <form onSubmit={save} className="mt-3 max-w-5xl space-y-3" data-testid="setup-form">
                <Section id="brand" title="1 · Brand kit" tone={TONE} testid="setup-brand-kit" pad={false}
                    note="the pictures that carry your name">
                    {BRAND.map(([kind, label, hint, size]) => (
                        <FieldRow key={kind} label={label} hint={hint} testid={`setup-brand-${kind}`}>
                            <div className="flex items-center gap-3">
                                <button type="button" onClick={() => refs[kind].current?.click()}
                                    className={`flex ${size} items-center justify-center overflow-hidden rounded-lg border-2 border-dashed border-black/20 hover:border-zinc-900`}
                                    data-testid={`setup-${kind}-btn`}>
                                    {uploading === kind ? <Loader2 size={15} className="animate-spin text-zinc-500" />
                                        : form[kind] ? <img src={mediaUrl(form[kind])} alt="" className="h-full w-full object-cover" />
                                            : <ImagePlus size={15} className="text-zinc-600" />}
                                </button>
                                <span className="text-xs text-[#4a4a4a]">{form[kind] ? "Click to replace" : "Click to upload"}</span>
                                <input ref={refs[kind]} type="file" accept="image/*" className="hidden" onChange={(e) => pick(kind, e.target.files?.[0])} />
                            </div>
                        </FieldRow>
                    ))}
                </Section>

                <Section id="basics" title="2 · The basics" tone={TONE} testid="setup-basics-block" pad={false}
                    note="name, hours, where to find you">
                    {FIELDS.map(([key, label, ph, hint]) => (
                        <FieldRow key={key} label={label} hint={hint} testid={`setup-row-${key}`}>{text(key, ph)}</FieldRow>
                    ))}
                    <FieldRow label="About the shop" hint="What you print, what machines you run, who you've worked with." top>
                        <textarea className={inputCls} rows={3} placeholder="What you print, what machines you run, who you've worked with…" value={form.about || ""} onChange={set("about")} data-testid="setup-about" />
                    </FieldRow>
                    <FieldRow label="Policies" hint="Deposits, artwork requirements, returns, rush fees." top>
                        <textarea className={inputCls} rows={3} placeholder="Deposits, artwork requirements, returns, rush fees…" value={form.policies || ""} onChange={set("policies")} data-testid="setup-policies" />
                    </FieldRow>
                </Section>

                <Section id="counters" title="3 · What I sell" tone={TONE} testid="setup-counters-block" pad={false}
                    note="switch a counter off and shoppers stop being offered it">
                    {lineDefs.map((l) => (
                        <FieldRow key={l.key} label={l.label} hint={l.note} testid={`setup-line-${l.key}-row`}>
                            <div className="flex flex-wrap items-center gap-3">
                                <Toggle on={lines[l.key] !== false} testid={`setup-line-${l.key}`}
                                    onChange={(v) => setLines((x) => ({ ...x, [l.key]: v }))} />
                                {l.key === "dtf" && (
                                    <Link to="/account/gang-sheets" data-testid="setup-gang-link"
                                        className="font-mono text-[11px] uppercase tracking-[0.14em] text-zinc-900 underline">
                                        set my films & price a foot
                                    </Link>
                                )}
                            </div>
                        </FieldRow>
                    ))}
                    {!me?.store?.designers && (
                        <p className="border-t border-[#e5e5e5] px-2.5 py-2 text-xs text-zinc-700">
                            The live designers switch on with a Pro Shop. Your choices save either way.
                        </p>
                    )}
                </Section>

                <Section id="ops" title="4 · Pickup & drop-off" tone={TONE} testid="setup-pickup-block" pad={false}
                    note="whatever you leave blank doesn't show">
                    {PICKUP_FLAGS.map(([key, label, hint]) => (
                        <FieldRow key={key} label={label} hint={hint} testid={`setup-${key.replace("_enabled", "")}-row`}>
                            <Toggle on={!!form[key]} onChange={flag(key)} testid={`setup-${key.replace("_enabled", "")}`} />
                        </FieldRow>
                    ))}
                    {PICKUP_FIELDS.map(([key, label, ph]) => (
                        <FieldRow key={key} label={label} testid={`setup-row-${key}`}>{text(key, ph)}</FieldRow>
                    ))}
                </Section>

                <Section id="shipping" title="5 · Shipping" tone={TONE} testid="setup-shipping-block" pad={false}
                    note="what an out-of-town customer needs to know">
                    <FieldRow label="Shipping promise" hint="The line shoppers see at checkout.">
                        <select className={inputCls} value={form.shipping_mode || ""} onChange={set("shipping_mode")} data-testid="setup-shipping-mode">
                            {SHIPPING.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                    </FieldRow>
                    {SHIP_FIELDS.map(([key, label, ph]) => (
                        <FieldRow key={key} label={label} testid={`setup-row-${key}`}>{text(key, ph)}</FieldRow>
                    ))}
                </Section>

                <Section id="socials" title="6 · Social profiles" tone={TONE} testid="setup-socials-block" pad={false}
                    note="handle or full link — either works">
                    {SOCIALS.map(([key, label, ph]) => (
                        <FieldRow key={key} label={label} testid={`setup-row-${key}`}>{text(key, ph)}</FieldRow>
                    ))}
                </Section>

                <Section id="billboard" title="7 · Your store billboard" tone={TONE} testid="setup-board-block" pad={false}
                    note="fill the fields above and your sign writes itself">
                    <FieldRow label="Billboard button" hint="Every store billboard reads the same way — badge, name, one-liner, paragraph, address, hours, pickup, shipping, then this button.">
                        <select className={inputCls} value={form.billboard_button || ""}
                            onChange={set("billboard_button")} data-testid="setup-billboard-button">
                            <option value="">Open my store (default)</option>
                            {MENU_ITEMS.filter((m) => m.key !== "store" && !m.legacy).map((m) => (
                                <option key={m.key} value={m.key}>{m.label}</option>
                            ))}
                        </select>
                    </FieldRow>
                    <FieldRow label="Your main product" hint="The product your shop page opens on.">
                        <select className={inputCls} data-testid="setup-featured"
                            value={`${featured.kind}|${featured.slug}`}
                            onChange={(e) => {
                                const [kind, slug] = e.target.value.split("|");
                                setFeatured({ ...featured, kind, slug: slug || "" });
                            }}>
                            <option value="|">Let 13thav pick for me</option>
                            {options.map((o) => <option key={`${o.kind}|${o.slug}`} value={`${o.kind}|${o.slug}`}>{o.label}</option>)}
                        </select>
                    </FieldRow>
                    <FieldRow label="Banner line" hint="One line over your main product.">
                        <input className={inputCls} placeholder="Detroit's fastest gang sheets."
                            value={featured.headline} onChange={(e) => setFeatured({ ...featured, headline: e.target.value })}
                            data-testid="setup-featured-headline" />
                    </FieldRow>
                </Section>

                <Section id="paint" title="8 · Your three colors" tone={TONE} testid="setup-theme-block" pad={false}
                    note="text is always black or white">
                    {[["primary", "Primary", "Buttons and highlights."], ["secondary", "Secondary", "Billboard and hero background."], ["tertiary", "Third", "Tags and small labels."]]
                        .map(([key, label, hint]) => (
                            <FieldRow key={key} label={label} hint={hint} testid={`setup-theme-${key}-row`}>
                                <div className="flex items-center gap-2">
                                    <input type="color" value={theme[key]} onChange={(e) => setTheme({ ...theme, [key]: e.target.value })}
                                        data-testid={`setup-theme-${key}`}
                                        className="h-8 w-11 cursor-pointer rounded border border-black/15 bg-transparent" />
                                    <input value={theme[key]} onChange={(e) => setTheme({ ...theme, [key]: e.target.value })}
                                        className="w-24 rounded border border-black/15 bg-white px-1.5 py-1 font-mono text-xs text-zinc-900" />
                                </div>
                            </FieldRow>
                        ))}
                </Section>

                <Section id="money" title="9 · Money & the Gang club" tone={TONE} testid="setup-money-block" pad={false}
                    note="your register and the 13thav wholesale club">
                    <FieldRow label="Payouts & card payments" hint="Connect your own Stripe account so your register can take cards.">
                        <Link to="/account/payouts" data-testid="setup-payouts-link"
                            className="inline-flex items-center gap-2 border border-[#d0d0d0] bg-white px-2.5 py-1.5 text-sm font-semibold text-[#1a1a1a] hover:border-[#1a1a1a]">
                            <CreditCard size={14} /> Open payouts <ArrowRight size={13} />
                        </Link>
                    </FieldRow>
                    <FieldRow label="Honor Gang member discounts" hint="Gang members get their member price on your products and every listing carries a Gang prices honored badge — that's how the club's shoppers find you. 13thav keeps the membership fees; you keep the customer and the order.">
                        <Toggle on={!!form.crew_perks_honored} onChange={flag("crew_perks_honored")} testid="setup-crew-perks" />
                    </FieldRow>
                </Section>

                <div className="sticky bottom-0 z-30 -mx-3.5 mt-3 flex flex-wrap items-center gap-2 border-t border-black/10 bg-white/95 px-3.5 py-2 backdrop-blur sm:-mx-5 sm:px-5">
                    <Btn type="submit" tone="solid" disabled={busy} testid="setup-save">
                        {busy ? <Loader2 className="animate-spin" size={11} /> : <Save size={11} />} Save store
                    </Btn>
                    {me?.store && <Link to={`/stores/${me.store.slug}`}><Btn testid="setup-view">View my page</Btn></Link>}
                    <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-zinc-700">
                        one save covers every panel on this screen
                    </p>
                </div>
            </form>
        </BackOffice>
    );
}
